import React from 'react';
import { intro, paragraph1, paragraph2 } from '../../bio';
import { Typography, Box, Container, Paper } from '@mui/material';

type Props = {};

const BioPageLayout = (props: Props) => {
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h1" 
          sx={{ 
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700,
            color: '#2c3e50',
            mb: 2,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          Biography
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontSize: { xs: '1.1rem', md: '1.25rem' },
            color: '#7f8c8d',
            fontStyle: 'italic',
            fontFamily: 'Georgia, serif'
          }}
        >
          The Journey of Philip Payne
        </Typography>
      </Box>

      {/* First Section */}
      <Paper 
        elevation={0}
        sx={{ 
          mb: 6,
          p: { xs: 3, md: 5 },
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: 'center'
        }}>
          <Box 
            sx={{ 
              flex: { xs: '1', md: '0 0 40%' },
              width: { xs: '100%', md: 'auto' }
            }}
          >
            <img 
              src='/phil-holding-books.jpeg' 
              alt='Phil with his first two books' 
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                objectFit: 'cover'
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography 
              sx={{ 
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.9,
                color: '#444',
                fontFamily: 'Georgia, serif',
                textAlign: 'justify',
                '&::first-letter': {
                  fontSize: '3.5em',
                  fontWeight: 'bold',
                  lineHeight: 0.8,
                  float: 'left',
                  marginRight: '8px',
                  marginTop: '4px',
                  color: '#e74c3c'
                }
              }}
            >
              {intro}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Second Section */}
      <Paper 
        elevation={0}
        sx={{ 
          mb: 6,
          p: { xs: 3, md: 5 },
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row-reverse' },
          gap: 4,
          alignItems: 'center'
        }}>
          <Box 
            sx={{ 
              flex: { xs: '1', md: '0 0 40%' },
              width: { xs: '100%', md: 'auto' }
            }}
          >
            <img 
              src='/portrait.jpeg' 
              alt='Phil looking content' 
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                objectFit: 'cover'
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', md: '2rem' },
                fontWeight: 600,
                color: '#2c3e50',
                mb: 3,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
            >
              Early Years
            </Typography>
            <Typography 
              sx={{ 
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.9,
                color: '#444',
                fontFamily: 'Georgia, serif',
                textAlign: 'justify'
              }}
            >
              {paragraph1}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Third Section */}
      <Paper 
        elevation={0}
        sx={{ 
          mb: 6,
          p: { xs: 3, md: 5 },
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: 'center'
        }}>
          <Box 
            sx={{ 
              flex: { xs: '1', md: '0 0 40%' },
              width: { xs: '100%', md: 'auto' }
            }}
          >
            <img 
              src='/phil-concentrating.jpeg' 
              alt='Phil concentrating over a table' 
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                objectFit: 'cover'
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', md: '2rem' },
                fontWeight: 600,
                color: '#2c3e50',
                mb: 3,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
            >
              The Writing Journey
            </Typography>
            <Typography 
              sx={{ 
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.9,
                color: '#444',
                fontFamily: 'Georgia, serif',
                textAlign: 'justify'
              }}
            >
              {paragraph2}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Final Image */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Box
          component="img"
          src="/phil-mug.jpeg"
          alt="Phil holding a mug"
          sx={{
            maxWidth: '500px',
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        />
      </Box>
    </Container>
  );
};

export default BioPageLayout;
